import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { fonteStatusLista } from '../components/atendimento/statusLista';
import {
  cancelamentosEntramNoAcionar, linhasAcionar, parceirosDoFiltro, TIPO_CANCELAMENTO_AES,
} from '../components/atendimento/acionarParceiro';
import { Search, Plus, Filter, X, Clock, CheckCircle, AlertCircle, FileText, RotateCcw, Download, FileSpreadsheet, CheckSquare, ExternalLink, Copy, ChevronDown, GitMerge, ArrowLeftRight, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mesmas grafias do formulário de atendimento (constants.js) — o filtro busca
// por igualdade exata, então divergência de grafia = filtro vazio.
const CATEGORIAS = [
  "Acompanhamento", "Arrependimento", "Assistência Técnica",
  "Comprovante de Entrega", "Falha Cadastro", "Falha Compras",
  "Falha Fornecedor", "Falha Integração", "Falha Parceiro",
  "Falha Produção", "Falha Transporte", "Impossibilidade de Entrega",
];

const MOTIVOS_PENDENCIA = [
  "Ag. Barrar",
  "Ag. Bseller",
  "Ag. Cliente",
  "Ag. Compras",
  "Ag. Conferência CSU",
  "Ag. Conferência LL",
  "Ag. Confirmação de Entrega",
  "Ag. Correios",
  "Ag. Fornecedor",
  "Ag. Logística",
  "Ag. Parceiro",
  "Ag. Transportadora - Asap",
  "Ag. Transportadora - J&T",
  "Ag. Transportadora - Total",
  "Aguardando",
  "Em devolução - Correios",
  "Em devolução - Transp.",
  "Enviado",
  "Atendido",
  "Devolvido",
  "Encerrado",
  "Entregue",
  "Estornado",
];

const ATENDENTES = ["Letícia Martelo", "Adnéia Campos"];

const ListaAtendimentos = () => {
  const [atendimentos, setAtendimentos] = useState([]);
  const [loading, setLoading] = useState(true);
  // Desenhar só um pedaço da lista (Adnéia, 17/10/2026: "quando tira o filtro ele carrega
  // todos os chamados e trava"). Sem filtro vinham 5.000 linhas de uma vez e a tela parava
  // até para clicar em "+ Novo atendimento". A lista inteira continua carregada: ordenar,
  // exportar e Acionar Parceiro usam todas; só o desenho vai de 100 em 100.
  const LINHAS_POR_VEZ = 100;
  const [limiteLinhas, setLimiteLinhas] = useState(LINHAS_POR_VEZ);
  // Cada busca ganha um número; só a mais nova escreve na tela (uma busca lenta que
  // chegasse depois apagaria o resultado da mais recente).
  const buscaAtual = useRef(0);
  const [globalFilter, setGlobalFilter] = useState('');
  const [searchType, setSearchType] = useState('todos'); // 'todos', 'solicitacao', 'entrega', 'cpf', 'nome'
  const [filters, setFilters] = useState({
    pendente: '',
    categoria: '',
    retornar_chamado: '',
    verificar_adneia: '',
    motivo_pendencia: [],
    parceiro: []
  });
  const [showFilters, setShowFilters] = useState(false);
  const [parceiros, setParceiros] = useState([]);
  const [motivosDisponiveis, setMotivosDisponiveis] = useState([]);

  const [totalNaBase, setTotalNaBase] = useState(null);
  const [cancelMap, setCancelMap] = useState({}); // {numero_pedido: {has_cancelamento, tipos, aviso_movimentou}}
  const [acionarData, setAcionarData] = useState(null); // modal "Acionar Parceiro"
  const [cancAgParceiro, setCancAgParceiro] = useState([]); // cancelamentos AES com ticket (Ag. Parceiro)

  // Estados para mesclar atendimentos
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeData, setMergeData] = useState(null); // { principal, secundario }
  const [merging, setMerging] = useState(false);

  // Estado de ordenação da tabela
  const [sortBy, setSortBy] = useState(null); // 'dias' | 'anotacao' | 'status_pedido'
  const [sortDir, setSortDir] = useState('desc');

  // Estados para finalização de atendimentos
  const [showFinalizarDialog, setShowFinalizarDialog] = useState(false);
  const [canaisSemAtividade, setCanaisSemAtividade] = useState([]);
  const [canaisComAtividade, setCanaisComAtividade] = useState([]);
  const [verificandoCanais, setVerificandoCanais] = useState(false);
  const [finalizando, setFinalizando] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { getAuthHeader, user } = useAuth();
  const [desmarcandoVerificar, setDesmarcandoVerificar] = useState(false);

  // Chave para localStorage dos filtros — POR USUÁRIO
  const FILTERS_STORAGE_KEY = `elo_atendimentos_filters_${user?.email || 'default'}`;

  // Flag para indicar se já carregou os filtros do localStorage
  const [filtersLoaded, setFiltersLoaded] = useState(false);

  // Restaurar filtros do localStorage ao carregar
  useEffect(() => {
    const savedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        const restoredFilters = parsed.filters || {
          pendente: '',
          categoria: '',
          retornar_chamado: '',
          verificar_adneia: '',
          motivo_pendencia: [],
          parceiro: []
        };
        // Migrar parceiro de string para array
        if (typeof restoredFilters.parceiro === 'string') {
          restoredFilters.parceiro = restoredFilters.parceiro ? [restoredFilters.parceiro] : [];
        }
        // Migrar motivo_pendencia de string para array
        if (typeof restoredFilters.motivo_pendencia === 'string') {
          restoredFilters.motivo_pendencia = restoredFilters.motivo_pendencia ? [restoredFilters.motivo_pendencia] : [];
        }
        setFilters(restoredFilters);
        setGlobalFilter(parsed.globalFilter || '');
        setSearchType(parsed.searchType || 'todos');
      } catch (e) {
        console.error('Erro ao restaurar filtros:', e);
      }
    }
    setFiltersLoaded(true);
  }, []);

  // Salvar filtros no localStorage quando mudarem (após carregamento inicial)
  useEffect(() => {
    if (!filtersLoaded) return;
    const filtersToSave = {
      filters,
      globalFilter,
      searchType
    };
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filtersToSave));
  }, [filters, globalFilter, searchType, filtersLoaded]);

  // Ler filtro da URL ao carregar (tem prioridade sobre localStorage)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const filterParam = searchParams.get('filter');
    if (filterParam === 'retornar') {
      setFilters(f => ({ ...f, retornar_chamado: 'true', pendente: '', verificar_adneia: '' }));
    } else if (filterParam === 'verificar') {
      setFilters(f => ({ ...f, verificar_adneia: 'true', pendente: '', retornar_chamado: '' }));
    }
  }, [location.search]);

  // Buscar dados apenas quando filtros carregados. Com texto na busca, espera a pessoa
  // parar de digitar (0,6 s) em vez de buscar a cada letra; Enter e "Buscar" vão na hora.
  useEffect(() => {
    if (!filtersLoaded) return;
    const t = setTimeout(() => fetchData(), globalFilter ? 600 : 0);
    return () => clearTimeout(t);
  }, [filters, globalFilter, searchType, filtersLoaded]);


  const fetchData = async () => {
    const minhaBusca = ++buscaAtual.current;
    try {
      const params = new URLSearchParams();
      if (filters.pendente !== '') params.append('pendente', filters.pendente);
      if (filters.categoria) params.append('categoria', filters.categoria);
      if (filters.retornar_chamado !== '') params.append('retornar_chamado', filters.retornar_chamado);
      if (filters.verificar_adneia !== '') params.append('verificar_adneia', filters.verificar_adneia);
      if (Array.isArray(filters.motivo_pendencia) && filters.motivo_pendencia.length > 0) params.append('motivo_pendencia', filters.motivo_pendencia.join(','));
      // AJUSTE 3: Suporte a múltiplos parceiros
      if (filters.parceiro) {
        if (Array.isArray(filters.parceiro)) {
          params.append('parceiro', filters.parceiro.join(','));
        } else {
          params.append('parceiro', filters.parceiro);
        }
      }
      if (globalFilter) {
        params.append('search', globalFilter);
        params.append('search_type', searchType);
      }

      // Params para busca de motivos disponíveis — igual ao principal mas SEM motivo_pendencia
      const motivosParams = new URLSearchParams();
      if (filters.pendente !== '') motivosParams.append('pendente', filters.pendente);
      if (filters.categoria) motivosParams.append('categoria', filters.categoria);
      if (filters.retornar_chamado !== '') motivosParams.append('retornar_chamado', filters.retornar_chamado);
      if (filters.verificar_adneia !== '') motivosParams.append('verificar_adneia', filters.verificar_adneia);
      if (filters.parceiro) {
        if (Array.isArray(filters.parceiro)) { motivosParams.append('parceiro', filters.parceiro.join(',')); }
        else { motivosParams.append('parceiro', filters.parceiro); }
      }
      if (globalFilter) {
        motivosParams.append('search', globalFilter);
        motivosParams.append('search_type', searchType);
      }

      // A lista de motivos p/ o multi-select só precisa de uma consulta EXTRA quando
      // há filtro de motivo ativo (aí a lista principal vem filtrada e não cobre todo
      // o contexto). Sem filtro de motivo, reaproveitamos a própria lista principal —
      // evita baixar a mesma lista 2x a cada carregamento.
      const temFiltroMotivo = Array.isArray(filters.motivo_pendencia) && filters.motivo_pendencia.length > 0;
      // Parceiros do filtro: só os que têm atendimento na lista da tela, em todo filtro (Adnéia,
      // 30/10/2026, por clique: "Em todos os filtros"; antes, só com motivo marcado, desde 28/10).
      // Com parceiro marcado, a lista principal vem cortada; aí uma consulta igual, sem o
      // parceiro, dá o contexto todo.
      const parceirosMarcados = Array.isArray(filters.parceiro) ? filters.parceiro : [];
      const parceirosParams = new URLSearchParams(params);
      parceirosParams.delete('parceiro');
      // Ag. Parceiro: os cancelamentos do AES com ticket entram no filtro e no Acionar Parceiro
      const comCancelamentos = cancelamentosEntramNoAcionar(filters, globalFilter);
      const [chamadosRes, totalRes, motivosRes, parceirosRes, cancRes] = await Promise.all([
        axios.get(`${API_URL}/api/chamados?${params.toString()}`, { headers: getAuthHeader() }),
        totalNaBase === null ? axios.get(`${API_URL}/api/admin/total-na-base`, { headers: getAuthHeader() }).catch(() => null) : Promise.resolve(null),
        temFiltroMotivo
          ? axios.get(`${API_URL}/api/chamados?${motivosParams.toString()}`, { headers: getAuthHeader() }).catch(() => null)
          : Promise.resolve(null),
        parceirosMarcados.length > 0
          ? axios.get(`${API_URL}/api/chamados?${parceirosParams.toString()}`, { headers: getAuthHeader() }).catch(() => null)
          : Promise.resolve(null),
        comCancelamentos
          ? axios.get(`${API_URL}/api/cancelamentos/ag-parceiro`, { headers: getAuthHeader() }).catch(() => null)
          : Promise.resolve(null)
      ]);

      if (minhaBusca !== buscaAtual.current) return;   // já chegou uma busca mais nova
      setAtendimentos(chamadosRes.data);
      setLimiteLinhas(LINHAS_POR_VEZ);
      if (totalRes?.data) {
        setTotalNaBase(totalRes.data.total_chamados);
      }

      // Alerta na lista: checa em lote quais entregas estão para cancelamento
      const entregasLote = [...new Set(chamadosRes.data.map(a => String(a.numero_pedido || '').split('.')[0]).filter(Boolean))];
      if (entregasLote.length) {
        axios.post(`${API_URL}/api/cancelamentos/check-lote`, { pedidos: entregasLote }, { headers: getAuthHeader() })
          .then(r => setCancelMap(r.data?.results || {}))
          .catch(() => setCancelMap({}));
      } else {
        setCancelMap({});
      }

      // Parceiros da lista filtrada (o filtro de parceiros mostra só estes)
      const cancelamentos = cancRes?.data?.cancelamentos || [];
      setCancAgParceiro(cancelamentos);
      setParceiros(parceirosDoFiltro(parceirosRes?.data || chamadosRes.data, cancelamentos, parceirosMarcados));
      // Motivos p/ o multi-select: da consulta extra (quando houve filtro de motivo)
      // ou, senão, do próprio resultado principal — que já é o contexto completo.
      const motivosFonte = motivosRes?.data || chamadosRes.data;
      const motivosUnicos = [...new Set(motivosFonte.map(a => a.motivo_pendencia).filter(m => m))].sort();
      setMotivosDisponiveis(motivosUnicos);
    } catch (error) {
      if (minhaBusca === buscaAtual.current) toast.error('Erro ao carregar atendimentos');
    } finally {
      if (minhaBusca === buscaAtual.current) setLoading(false);
    }
  };

  const handleSearch = () => {
    setLoading(true);
    fetchData();
  };

  const clearFilters = () => {
    setFilters({ pendente: '', categoria: '', retornar_chamado: '', verificar_adneia: '', motivo_pendencia: [], parceiro: [] });
    setGlobalFilter('');
    setSearchType('todos');
    localStorage.removeItem(FILTERS_STORAGE_KEY);
  };

  const hasActiveFilters = Object.values(filters).some(v => Array.isArray(v) ? v.length > 0 : v !== '') || globalFilter;

  // Função para exportar para Excel
  const exportToExcel = () => {
    if (atendimentos.length === 0) {
      toast.error('Nenhum atendimento para exportar');
      return;
    }

    try {
      // Preparar dados para exportação
      const dataToExport = atendimentos.map(atd => ({
        'Solicitação': atd.solicitacao || '',
        'Pedido': atd.numero_pedido || '',
        'Parceiro': atd.parceiro || atd.canal_vendas || '',
        'Nome': atd.nome_cliente || '',
        'Motivo Pendência': atd.motivo_pendencia || '',
        'Status Pedido': atd.status_pedido || '',
        'Data Último Ponto': atd.data_ultimo_status || '',
        'Fonte do Status': fonteStatusLista(atd).planilha,
        'Status': atd.pendente ? 'Pendente' : 'Resolvido',
        'Add. Transportadora': atd.transportadora || '',
        'Categoria': atd.categoria || '',
        'Motivo': atd.motivo || '',
        'Reversa': atd.codigo_reversa || '',
        'Anotações': atd.anotacoes || ''
      }));

      // Criar workbook e worksheet
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Atendimentos');

      // Ajustar largura das colunas
      ws['!cols'] = [
        { wch: 15 }, // Solicitação
        { wch: 12 }, // Pedido
        { wch: 15 }, // Parceiro
        { wch: 25 }, // Nome
        { wch: 20 }, // Motivo Pendência
        { wch: 25 }, // Status Pedido
        { wch: 18 }, // Data Último Ponto
        { wch: 20 }, // Fonte do Status
        { wch: 12 }, // Status
        { wch: 20 }, // Add. Transportadora
        { wch: 18 }, // Categoria
        { wch: 30 }, // Motivo
        { wch: 15 }, // Reversa
        { wch: 50 }, // Anotações
      ];

      // Nome do arquivo com data atual
      const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
      const fileName = `atendimentos_${dataAtual}.xlsx`;

      // Download
      XLSX.writeFile(wb, fileName);
      toast.success(`Exportado ${atendimentos.length} atendimentos para ${fileName}`);
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error('Erro ao exportar para Excel');
    }
  };

  // Função para verificar canais sem atividade
  const verificarCanais = async () => {
    setVerificandoCanais(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/atendimentos/verificar-canais`,
        { headers: getAuthHeader() }
      );

      setCanaisSemAtividade(response.data.canais_sem_atividade || []);
      setCanaisComAtividade(response.data.canais_com_atividade || []);
      setShowFinalizarDialog(true);
    } catch (error) {
      console.error('Erro ao verificar canais:', error);
      toast.error('Erro ao verificar canais');
    } finally {
      setVerificandoCanais(false);
    }
  };

  // Função para finalizar atendimentos do dia
  const finalizarAtendimentos = async () => {
    setFinalizando(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/atendimentos/finalizar-dia`,
        {},
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        toast.success('Atendimentos do dia finalizados com sucesso!');
        setShowFinalizarDialog(false);
      }
    } catch (error) {
      console.error('Erro ao finalizar:', error);
      toast.error('Erro ao finalizar atendimentos');
    } finally {
      setFinalizando(false);
    }
  };

  // Função para exportar relatório Ag. Compras (xlsx estilizado gerado no backend)
  const exportRelatorioCompras = async () => {
    try {
      toast.info('Gerando relatório Ag. Compras...');
      const response = await axios.get(
        `${API_URL}/api/relatorios/ag-compras-xlsx`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio_ag_compras_${dataAtual}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório Ag. Compras exportado');
    } catch (error) {
      console.error('Erro ao exportar relatório:', error);
      toast.error('Erro ao gerar relatório Ag. Compras');
    }
  };

  // Relatório Falha de Integração (xlsx estilizado gerado no backend)
  const exportRelatorioFalhaIntegracao = async () => {
    try {
      toast.info('Gerando relatório Falha de Integração...');
      const response = await axios.get(
        `${API_URL}/api/relatorios/falha-integracao-xlsx`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio_falha_integracao_${dataAtual}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório Falha de Integração exportado');
    } catch (error) {
      console.error('Erro ao exportar relatório:', error);
      toast.error('Erro ao gerar relatório Falha de Integração');
    }
  };

  // Função para exportar relatório Ag. Logística (xlsx estilizado gerado no backend)
  const exportRelatorioLogistica = async () => {
    try {
      toast.info('Gerando relatório Ag. Logística...');
      const response = await axios.get(
        `${API_URL}/api/relatorios/ag-logistica-xlsx`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio_ag_logistica_${dataAtual}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório Ag. Logística exportado');
    } catch (error) {
      console.error('Erro ao exportar relatório:', error);
      toast.error('Erro ao gerar relatório Ag. Logística');
    }
  };

  // Função para copiar dados da linha
  const copyRowData = (atd, e) => {
    e.stopPropagation(); // Não navegar ao clicar no botão

    const dadosParaCopiar = [
      `Entrega: ${atd.numero_pedido || '-'}`,
      `Cliente: ${atd.nome_cliente || '-'}`,
      `CPF: ${atd.cpf_cliente || '-'}`,
      `Parceiro: ${atd.parceiro || atd.canal_vendas || '-'}`,
      `Solicitação: ${atd.solicitacao || '-'}`,
      `Motivo Pendência: ${atd.motivo_pendencia || '-'}`,
      `Status Pedido: ${atd.status_pedido || '-'}`,
      `Reversa: ${atd.codigo_reversa || '-'}`,
      `Categoria: ${atd.categoria || '-'}`,
    ].join('\n');

    navigator.clipboard.writeText(dadosParaCopiar).then(() => {
      toast.success('Dados copiados!');
    }).catch(() => {
      toast.error('Erro ao copiar dados');
    });
  };

  const copyToClipboardHTTP = (text, onSuccess, onError) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(onSuccess).catch(onError);
    } else {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;z-index:9999';
      document.body.appendChild(el);
      el.focus();
      el.select();
      el.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      if (ok) onSuccess();
      else onError();
    }
  };

  // Função para copiar um texto específico
  const copyText = (text, label, e) => {
    if (e) e.stopPropagation();
    if (!text || text === '-') {
      toast.info('Nada para copiar');
      return;
    }
    copyToClipboardHTTP(text, () => toast.success(`${label} copiado!`), () => toast.error('Erro ao copiar'));
  };

  // Função para copiar todas as reversas dos atendimentos filtrados
  const copyTodasReversas = () => {
    const reversas = atendimentos
      .map(atd => atd.codigo_reversa || atd.reversa_codigo)
      .filter(r => r && r !== '-' && r.trim() !== '');

    if (reversas.length === 0) {
      toast.info('Nenhuma reversa encontrada nos atendimentos filtrados');
      return;
    }

    const textoReversas = reversas.join('\n');
    copyToClipboardHTTP(textoReversas, () => toast.success(`${reversas.length} reversa(s) copiada(s)!`), () => toast.error('Erro ao copiar reversas'));
  };

  // ── Acionar Parceiro: compila pendências filtradas para enviar ao canal ──
  // No Ag. Parceiro, os cancelamentos do AES com ticket vêm junto (acionarParceiro.js).
  const abrirAcionarParceiro = () => {
    const linhas = linhasAcionar(
      atendimentos,
      cancelamentosEntramNoAcionar(filters, globalFilter) ? cancAgParceiro : [],
      Array.isArray(filters.parceiro) ? filters.parceiro : [],
    );
    if (!linhas.length) { toast.info('Nenhum atendimento filtrado para acionar'); return; }
    setAcionarData(linhas);
  };
  const copiarAcionar = () => {
    const head = ['Parceiro', 'Tipo', 'Entrega', 'Pedido', 'Solicitação', 'CPF', 'Data', 'Última Anotação'].join('\t');
    const body = acionarData.map(r => [r.parceiro, r.tipo, r.entrega, r.pedido, r.solicitacao, r.cpf, r.data_anotacao, r.anotacao].join('\t')).join('\n');
    copyToClipboardHTTP(head + '\n' + body, () => toast.success(`${acionarData.length} pendência(s) copiada(s)!`), () => toast.error('Erro ao copiar'));
  };
  const exportarAcionar = async () => {
    try {
      // xlsx estilizado gerado no backend (título + cabeçalho colorido, como o da logística)
      const response = await axios.post(
        `${API_URL}/api/relatorios/acionar-parceiro-xlsx`,
        { linhas: acionarData },
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      // Nome do arquivo com o(s) parceiro(s) da lista: "Acionar Parceiro 2026-07-09 (CSU).xlsx"
      const unicos = [...new Set(acionarData.map(r => (r.parceiro || '').trim()).filter(p => p && p !== '-'))];
      const rotulo = unicos.length === 0 ? '' : unicos.length <= 3 ? unicos.join(' + ') : 'vários';
      const sufixo = rotulo ? ` (${rotulo.replace(/[\\/:*?"<>|]/g, '')})` : '';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Acionar Parceiro ${new Date().toISOString().slice(0, 10)}${sufixo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao exportar Acionar Parceiro:', error);
      toast.error('Erro ao gerar o arquivo');
    }
  };

  const getCategoryBadgeColor = (categoria) => {
    const colors = {
      'Falha Produção': 'bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-400',
      'Falha Compras': 'bg-orange-50 text-orange-700 dark:bg-orange-950/50 dark:text-orange-400',
      'Falha Transporte': 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400',
      'Falha Fornecedor': 'bg-lime-50 text-lime-700 dark:bg-lime-950/50 dark:text-lime-400',
      'Falha Parceiro': 'bg-fuchsia-50 text-fuchsia-700 dark:bg-fuchsia-950/50 dark:text-fuchsia-400',
      'Falha Cadastro': 'bg-teal-50 text-teal-700 dark:bg-teal-950/50 dark:text-teal-400',
      'Arrependimento': 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400',
      'Acompanhamento': 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950/50 dark:text-cyan-400',
      'Reclame Aqui': 'bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-400',
      'Assistência Técnica': 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-400',
      'Falha Integração': 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300',
      'Comprovante de Entrega': 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400',
      'Impossibilidade de Entrega': 'bg-violet-50 text-violet-700 dark:bg-violet-950/50 dark:text-violet-400',
    };
    return colors[categoria] || 'bg-slate-100 text-slate-700';
  };

  // Função para calcular o próximo dia útil
  const getProximoDiaUtil = () => {
    const hoje = new Date();
    const proximoDia = new Date(hoje);
    proximoDia.setDate(proximoDia.getDate() + 1);

    // Se cair no sábado (6), pula para segunda
    if (proximoDia.getDay() === 6) {
      proximoDia.setDate(proximoDia.getDate() + 2);
    }
    // Se cair no domingo (0), pula para segunda
    else if (proximoDia.getDay() === 0) {
      proximoDia.setDate(proximoDia.getDate() + 1);
    }

    return proximoDia.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  // Mapa de duplicatas: numero_pedido → lista de atendimentos pendentes
  const duplicatasMap = atendimentos
    .filter(a => a.pendente && a.numero_pedido)
    .reduce((acc, a) => {
      const key = a.numero_pedido;
      if (!acc[key]) acc[key] = [];
      acc[key].push(a);
      return acc;
    }, {});
  // Só conta como duplicata quando há 2+ pendentes para o mesmo pedido
  const pedidosDuplicados = new Set(
    Object.entries(duplicatasMap)
      .filter(([, lista]) => lista.length >= 2)
      .map(([key]) => key)
  );

  const handleOpenMerge = (e, atd) => {
    e.stopPropagation();
    const duplicatas = duplicatasMap[atd.numero_pedido] || [];
    const outro = duplicatas.find(d => d.id !== atd.id);
    if (!outro) return;
    // Mais antigo como principal, mais novo como secundário
    const [principal, secundario] = new Date(atd.data_abertura) <= new Date(outro.data_abertura)
      ? [atd, outro] : [outro, atd];
    setMergeData({ principal, secundario });
    setShowMergeDialog(true);
  };

  const handleInvertMerge = () => {
    if (!mergeData) return;
    setMergeData({ principal: mergeData.secundario, secundario: mergeData.principal });
  };

  const handleConfirmMerge = async () => {
    if (!mergeData) return;
    setMerging(true);
    try {
      await axios.post(
        `${API_URL}/api/chamados/${mergeData.principal.id_atendimento}/mesclar`,
        { id_secundario: mergeData.secundario.id_atendimento },
        { headers: getAuthHeader() }
      );
      toast.success(`${mergeData.secundario.id_atendimento} mesclado em ${mergeData.principal.id_atendimento}`);
      setShowMergeDialog(false);
      setMergeData(null);
      // Recarrega a lista (30/10/2026): antes chamava uma função que não existe, e a mescla
      // que deu certo mostrava "Erro ao mesclar atendimentos" e não recarregava.
      fetchData();
    } catch (err) {
      toast.error('Erro ao mesclar atendimentos');
    } finally {
      setMerging(false);
    }
  };

  // Stats
  const totalPendentes = atendimentos.filter(a => a.pendente).length;
  const totalResolvidos = atendimentos.filter(a => !a.pendente).length;
  const totalRetornar = atendimentos.filter(a => a.retornar_chamado).length;
  const totalVerificarAdneia = atendimentos.filter(a => a.verificar_adneia).length;

  // Calcular criados e fechados hoje
  const hoje = new Date();
  const hojeStr = hoje.toISOString().split('T')[0]; // YYYY-MM-DD

  const criadosHoje = atendimentos.filter(a => {
    if (!a.data_abertura) return false;
    const dataAbertura = a.data_abertura.split('T')[0];
    return dataAbertura === hojeStr;
  }).length;

  const fechadosHoje = atendimentos.filter(a => {
    if (!a.data_fechamento || a.pendente) return false;
    const dataFechamento = a.data_fechamento.split('T')[0];
    return dataFechamento === hojeStr;
  }).length;

  // Aberto para amanhã = Pendentes + Criados Hoje - Fechados Hoje
  // Na verdade, é só o total de pendentes pois já reflete o saldo
  const abertoParaAmanha = totalPendentes;
  const proximoDiaUtil = getProximoDiaUtil();

  // --- Ordenação de colunas ---
  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(col);
      setSortDir('desc'); // padrão: mais recente/maior primeiro
    }
  };

  const parseAnotacaoDate = (anotacoes) => {
    if (!anotacoes) return 0;
    const linha = anotacoes.split('\n')[0];
    const m = linha.match(/^\[(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\]/) ||
              linha.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\s*[-–]/);
    if (!m) return 0;
    const dia = parseInt(m[1]), mes = parseInt(m[2]);
    const ano = m[3] ? (parseInt(m[3]) < 100 ? 2000 + parseInt(m[3]) : parseInt(m[3])) : 2026;
    return new Date(ano, mes - 1, dia).getTime();
  };

  const parseStatusDate = (data_ultimo_status) => {
    if (!data_ultimo_status) return 0;
    const d = data_ultimo_status;
    if (d.includes('/')) {
      const parts = d.split(' ')[0].split('/');
      if (parts.length === 3) return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0])).getTime();
    }
    try { const dt = new Date(d); if (!isNaN(dt)) return dt.getTime(); } catch {}
    return 0;
  };

  // Dias úteis entre um timestamp e hoje (ignora sáb/dom)
  const diasUteisDesdeTs = (t) => {
    if (!t) return null;
    const ini = new Date(t); ini.setHours(0, 0, 0, 0);
    const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
    let dias = 0;
    const d = new Date(ini);
    while (d < hoje) {
      d.setDate(d.getDate() + 1);
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) dias++;
    }
    return dias;
  };
  const diasUteisDesde = (dataStr) => diasUteisDesdeTs(parseStatusDate(dataStr));
  // Data da última anotação REAL (ignora a nota automática "Entrou como crítico")
  const parseAnotacaoDataRelevante = (anotacoes) => {
    const linhas = (anotacoes || '').split('\n');
    for (const linha of linhas) {
      if (linha.includes('Entrou como crítico')) continue;
      const m = linha.match(/^\[(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\]/) ||
                linha.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\s*[-–]/);
      if (m) {
        const dia = parseInt(m[1]), mes = parseInt(m[2]);
        const ano = m[3] ? (parseInt(m[3]) < 100 ? 2000 + parseInt(m[3]) : parseInt(m[3])) : 2026;
        return new Date(ano, mes - 1, dia).getTime();
      }
    }
    return 0;
  };
  // Limite de dias úteis por motivo (null = não monitora)
  const limiteTravado = (atd) => {
    const m = atd.motivo_pendencia || '';
    if (m === 'Enviado') return 3;
    if (m === 'Ag. Logística') return 8;
    if (m === 'Ag. Fornecedor') return 3;
    if (m === 'Ag. Cliente') return 3;
    if (m === 'Ag. Bseller') return 3;
    if (m === 'Ag. Barrar') return 6;
    return null;
  };
  // Dias travado conforme a fonte de cada motivo:
  //  - Enviado / Ag. Logística → movimentação do pedido (data_ultimo_status)
  //  - Ag. Fornecedor → última anotação (tempo no motivo aguardando o fornecedor)
  // Flags de criticidade definidas pelo backend (sync) por motivo
  // Flag de criticidade do MOTIVO ATUAL (evita flag preso de motivo anterior — ex.: chamado
  // que estava em Ag. Logística >8d.ú., movimentou e virou Enviado: o flag de logística não
  // deve mais marcá-lo como travado).
  const FLAG_POR_MOTIVO = {
    'Enviado': 'verificar_travado_auto',
    'Ag. Logística': 'verificar_logistica_auto',
    'Ag. Fornecedor': 'verificar_fornecedor_auto',
    'Ag. Compras': 'verificar_compras_auto',
    'Ag. Cliente': 'verificar_cliente_auto',
    'Ag. Bseller': 'verificar_bseller_auto',
    'Ag. Barrar': 'verificar_barrar_auto',
  };
  const flaggadoCritico = (atd) => {
    const m = atd.motivo_pendencia || '';
    if (m.startsWith('Ag. Transportadora')) return !!atd.verificar_transp_auto;
    const f = FLAG_POR_MOTIVO[m];
    return f ? !!atd[f] : false;
  };
  // Motivos cujo "tempo" é medido pela última anotação (não pela movimentação do pedido)
  const ANOTACAO_BASED = ['Ag. Fornecedor', 'Ag. Cliente', 'Ag. Bseller', 'Ag. Barrar', 'Ag. Transportadora - J&T', 'Ag. Transportadora - Total', 'Ag. Transportadora - Asap'];
  const diasTravado = (atd) => {
    const m = atd.motivo_pendencia || '';
    if (ANOTACAO_BASED.includes(m)) return diasUteisDesdeTs(parseAnotacaoDataRelevante(atd.anotacoes));
    const porMov = diasUteisDesde(atd.data_ultimo_status);
    if (porMov !== null) return porMov;
    // Fallback: extrai "X dias úteis" da nota automática (ex.: Ag. Compras)
    const m2 = (atd.anotacoes || '').match(/(\d+)\s*dias úteis/);
    return m2 ? parseInt(m2[1]) : null;
  };
  // Atendimento travado:
  //  - Motivos COM limite no front (Enviado, Ag. Logística, Fornecedor, Cliente, Bseller, Barrar):
  //    recalcula SEMPRE pela movimentação/anotação atual. Ignora flag preso do backend — o flag
  //    não é limpo quando o pedido movimenta, então sozinho daria falso "Travado" (ex.: parou >8d.ú.
  //    em Logística, movimentou ontem → não está mais travado).
  //  - Motivos SEM limite no front (Ag. Compras por prazo do fornecedor, Ag. Transportadora):
  //    confia no flag do backend (do motivo atual).
  const travadoEnviado = (atd) => {
    const lim = limiteTravado(atd);
    if (lim === null) return flaggadoCritico(atd);
    const du = diasTravado(atd);
    return du !== null && du > lim;
  };
  // Tira o Verificar de todos (Adnéia, 18/10/2026: o Verificar passa a ser só manual, para
  // cliente em atrito). Só a gestão vê o botão; o backend também confere.
  const desmarcarTodosVerificar = async (e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Tirar o "Verificar" de TODOS os atendimentos marcados?\nNão muda status, motivo nem anotação.')) return;
    setDesmarcandoVerificar(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/verificar/desmarcar-todos`, {}, { headers: getAuthHeader() });
      setAtendimentos(prev => prev.map(a => (a.verificar_adneia ? { ...a, verificar_adneia: false } : a)));
      toast.success(`Verificar removido de ${r.data?.desmarcados ?? 0} atendimento(s).`);
    } catch (err) {
      toast.error('Não desmarcou: ' + (err?.response?.data?.detail || err?.message || ''));
    } finally {
      setDesmarcandoVerificar(false);
    }
  };

  // Marca/desmarca "Verificar" inline (sem abrir o atendimento)
  const toggleVerificar = async (atd, e) => {
    if (e) e.stopPropagation();
    const novo = !atd.verificar_adneia;
    try {
      await axios.put(`${API_URL}/api/chamados/${atd.id}`, { verificar_adneia: novo }, { headers: getAuthHeader() });
      setAtendimentos(prev => prev.map(a => a.id === atd.id ? { ...a, verificar_adneia: novo } : a));
      toast.success(novo ? 'Marcado como Verificar' : 'Verificar removido');
    } catch {
      toast.error('Erro ao atualizar');
    }
  };

  // Checkpoint "Aguardando": cliente postou → move para Em devolução + planilha de devolução
  const marcarPostado = async (atd, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Confirmar que o cliente postou o item?\nIsso move para "Em devolução" e registra na planilha de gestão de devolução.')) return;
    try {
      const r = await axios.post(`${API_URL}/api/chamados/${atd.id}/marcar-postado`, {}, { headers: getAuthHeader() });
      const novo = r.data?.motivo_pendencia || 'Em devolução - Correios';
      setAtendimentos(prev => prev.map(a => a.id === atd.id ? { ...a, reversa_postada: true, motivo_pendencia: novo } : a));
      toast.success(`Postado → ${novo} · planilha de devolução atualizada`);
    } catch {
      toast.error('Erro ao marcar como postado');
    }
  };

  const sortedAtendimentos = sortBy ? [...atendimentos].sort((a, b) => {
    let valA = 0, valB = 0;
    if (sortBy === 'dias') { valA = a.dias_aberto ?? 0; valB = b.dias_aberto ?? 0; }
    else if (sortBy === 'anotacao') { valA = parseAnotacaoDate(a.anotacoes); valB = parseAnotacaoDate(b.anotacoes); }
    else if (sortBy === 'status_pedido') { valA = parseStatusDate(a.data_ultimo_status); valB = parseStatusDate(b.data_ultimo_status); }
    return sortDir === 'desc' ? valB - valA : valA - valB;
  }) : atendimentos;

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDir === 'desc'
      ? <ArrowDown className="h-3 w-3 ml-1 text-primary" />
      : <ArrowUp className="h-3 w-3 ml-1 text-primary" />;
  };

  const qtdCancAcionar = (acionarData || []).filter(r => r.tipo === TIPO_CANCELAMENTO_AES).length;

  if (loading) {
    return (
      <div className="space-y-6" data-testid="loading">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lista-atendimentos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-['Plus_Jakarta_Sans']">Atendimentos</h1>
          <p className="text-muted-foreground text-sm">{atendimentos.length} atendimentos encontrados</p>
          {/* O backend devolve no máximo 5.000 (os mais novos): sem filtro, os antigos ficam de fora. */}
          {atendimentos.length >= 5000 && (
            <p className="text-amber-700 dark:text-amber-400 text-xs">A lista para nos 5.000 mais recentes. Para achar um mais antigo, use a busca ou um filtro.</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Botão para copiar todas as reversas */}
          <Button
            variant="outline"
            onClick={copyTodasReversas}
            className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200"
            data-testid="btn-copiar-reversas"
          >
            <Copy className="h-4 w-4 mr-2" />
            Copiar Reversas
          </Button>
          {/* Acionar Parceiro: compila pendências filtradas (Ag. Parceiro) para enviar ao canal */}
          <Button
            variant="outline"
            onClick={abrirAcionarParceiro}
            className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200"
            data-testid="btn-acionar-parceiro"
          >
            <FileText className="h-4 w-4 mr-2" />
            Acionar Parceiro
          </Button>
          {/* Relatórios Especiais */}
          <Button variant="outline" onClick={exportRelatorioCompras} className="bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-200" data-testid="btn-relatorio-compras">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Relatório Ag. Compras
          </Button>
          <Button variant="outline" onClick={exportRelatorioLogistica} className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200" data-testid="btn-relatorio-logistica">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Relatório Ag. Logística
          </Button>
          <Button variant="outline" onClick={exportRelatorioFalhaIntegracao} className="bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200" data-testid="btn-relatorio-falha-integracao">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Relatório Falha Integração
          </Button>
          <Button variant="outline" onClick={exportToExcel} data-testid="btn-exportar">
            <Download className="h-4 w-4 mr-2" />
            Exportar Excel
          </Button>
          <Button onClick={() => navigate('/chamados/novo')} data-testid="btn-novo">
            <Plus className="h-4 w-4 mr-2" />
            Novo Atendimento
          </Button>
        </div>
      </div>

      {/* Dialog de Mesclar Atendimentos */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-amber-500" />
              Mesclar Atendimentos
            </DialogTitle>
            <DialogDescription>
              Dois atendimentos do mesmo pedido serão unificados. O secundário será removido e suas informações adicionadas às anotações do principal.
            </DialogDescription>
          </DialogHeader>
          {mergeData && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="border rounded-lg p-3 bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
                  <p className="text-xs font-semibold text-green-700 dark:text-green-400 mb-1">PRINCIPAL (mantido)</p>
                  <p className="font-mono text-sm font-bold">{mergeData.principal.id_atendimento}</p>
                  <p className="text-xs text-muted-foreground">{mergeData.principal.motivo_pendencia || '-'}</p>
                  <p className="text-xs text-muted-foreground truncate">{mergeData.principal.solicitacao || '-'}</p>
                </div>
                <div className="border rounded-lg p-3 bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800">
                  <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-1">SECUNDÁRIO (removido)</p>
                  <p className="font-mono text-sm font-bold">{mergeData.secundario.id_atendimento}</p>
                  <p className="text-xs text-muted-foreground">{mergeData.secundario.motivo_pendencia || '-'}</p>
                  <p className="text-xs text-muted-foreground truncate">{mergeData.secundario.solicitacao || '-'}</p>
                </div>
              </div>
              <button
                onClick={handleInvertMerge}
                className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground mx-auto"
              >
                <ArrowLeftRight className="h-3 w-3" />
                Inverter principal e secundário
              </button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMergeDialog(false)} disabled={merging}>
              Cancelar
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={handleConfirmMerge}
              disabled={merging}
            >
              {merging ? 'Mesclando...' : 'Confirmar Mesclagem'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Finalização de Atendimentos */}
      <Dialog open={showFinalizarDialog} onOpenChange={setShowFinalizarDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Finalizar Atendimentos do Dia</DialogTitle>
            <DialogDescription>
              Verificação de atividade nos canais diários
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {canaisSemAtividade.length > 0 ? (
              <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                <p className="font-semibold text-amber-800 dark:text-amber-200 mb-2">
                  ⚠️ Canais sem atividade hoje:
                </p>
                <ul className="list-disc list-inside space-y-1">
                  {canaisSemAtividade.map((canal, idx) => (
                    <li key={idx} className="text-amber-700 dark:text-amber-300">{canal}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                <p className="font-semibold text-green-800 dark:text-green-200">
                  ✅ Todos os canais tiveram atividade hoje!
                </p>
              </div>
            )}

            {canaisComAtividade.length > 0 && (
              <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-900/30 border">
                <p className="font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Canais com atividade ({canaisComAtividade.length}):
                </p>
                <p className="text-sm text-muted-foreground">
                  {canaisComAtividade.map(c => typeof c === 'string' ? c : c.canal).join(', ')}
                </p>
              </div>
            )}

            <p className="text-sm text-muted-foreground">
              {canaisSemAtividade.length > 0
                ? 'Deseja finalizar mesmo assim? Os canais sem atividade serão registrados.'
                : 'Deseja confirmar a finalização dos atendimentos do dia?'
              }
            </p>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowFinalizarDialog(false)}>
              Revisar
            </Button>
            <Button
              onClick={finalizarAtendimentos}
              disabled={finalizando}
              className="bg-green-600 hover:bg-green-700"
            >
              {finalizando ? 'Finalizando...' : 'Confirmar Finalização'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => setFilters(f => ({ ...f, pendente: 'true', retornar_chamado: '', verificar_adneia: '' }))}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-md bg-amber-100 dark:bg-amber-900/30">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalPendentes}</p>
              <p className="text-sm text-muted-foreground">Pendentes</p>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => setFilters(f => ({ ...f, retornar_chamado: 'true', pendente: '', verificar_adneia: '' }))}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-md bg-rose-100 dark:bg-rose-900/30">
              <AlertCircle className="h-5 w-5 text-rose-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalRetornar}</p>
              <p className="text-sm text-muted-foreground">Retornar</p>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => setFilters(f => ({ ...f, verificar_adneia: 'true', pendente: '', retornar_chamado: '' }))}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-md bg-purple-100 dark:bg-purple-900/30">
              <AlertCircle className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalVerificarAdneia}</p>
              <p className="text-sm text-muted-foreground">Verificar</p>
              {user?.role === 'admin' && totalVerificarAdneia > 0 && (
                <button type="button" onClick={desmarcarTodosVerificar} disabled={desmarcandoVerificar}
                  className="mt-1 text-xs font-semibold text-purple-700 dark:text-purple-300 underline disabled:opacity-60"
                  data-testid="btn-desmarcar-todos-verificar">
                  {desmarcandoVerificar ? 'Desmarcando…' : 'Desmarcar todos'}
                </button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => setFilters(f => ({ ...f, pendente: 'false', retornar_chamado: '', verificar_adneia: '' }))}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-md bg-emerald-100 dark:bg-emerald-900/30">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalResolvidos}</p>
              <p className="text-sm text-muted-foreground">Resolvidos</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700" data-testid="card-total-base">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-md bg-amber-200 dark:bg-amber-900/50">
              <FileText className="h-5 w-5 text-amber-700" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-800 dark:text-amber-300">{totalNaBase !== null ? totalNaBase : '...'}</p>
              <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">Total na Base</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <Select value={searchType} onValueChange={setSearchType}>
                <SelectTrigger className="w-40" data-testid="select-search-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="solicitacao">Solicitação</SelectItem>
                  <SelectItem value="entrega">Entrega</SelectItem>
                  <SelectItem value="cpf">CPF</SelectItem>
                  <SelectItem value="nome">Nome</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={
                    searchType === 'solicitacao' ? 'Buscar por nº da solicitação...' :
                    searchType === 'entrega' ? 'Buscar por nº da entrega...' :
                    searchType === 'cpf' ? 'Buscar por CPF do cliente...' :
                    searchType === 'nome' ? 'Buscar por nome do cliente...' :
                    'Buscar por entrega, CPF, nome, solicitação...'
                  }
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-9"
                  data-testid="input-search"
                />
              </div>
              <Button variant="secondary" onClick={handleSearch} data-testid="btn-buscar">
                <Search className="h-4 w-4 mr-2" />
                Buscar
              </Button>
              <Button
                variant={showFilters ? 'secondary' : 'outline'}
                onClick={() => setShowFilters(!showFilters)}
                data-testid="btn-filtros"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filtros
              </Button>
              {hasActiveFilters && (
                <Button variant="ghost" onClick={clearFilters} data-testid="btn-limpar">
                  <X className="h-4 w-4 mr-2" />
                  Limpar
                </Button>
              )}
            </div>

            {showFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 pt-4 border-t">
                <Select
                  value={
                    filters.retornar_chamado === 'true' ? 'retornar' :
                    filters.verificar_adneia === 'true' ? 'verificar' :
                    filters.pendente === 'true' ? 'pendente' :
                    filters.pendente === 'false' ? 'resolvido' : 'all'
                  }
                  onValueChange={(v) => {
                    if (v === 'retornar') {
                      setFilters(f => ({ ...f, pendente: '', retornar_chamado: 'true', verificar_adneia: '' }));
                    } else if (v === 'verificar') {
                      setFilters(f => ({ ...f, pendente: '', retornar_chamado: '', verificar_adneia: 'true' }));
                    } else if (v === 'pendente') {
                      setFilters(f => ({ ...f, pendente: 'true', retornar_chamado: '', verificar_adneia: '' }));
                    } else if (v === 'resolvido') {
                      setFilters(f => ({ ...f, pendente: 'false', retornar_chamado: '', verificar_adneia: '' }));
                    } else {
                      setFilters(f => ({ ...f, pendente: '', retornar_chamado: '', verificar_adneia: '' }));
                    }
                  }}
                >
                  <SelectTrigger data-testid="filter-pendente">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="pendente">Pendentes</SelectItem>
                    <SelectItem value="retornar">Retornar</SelectItem>
                    <SelectItem value="verificar">Verificar</SelectItem>
                    <SelectItem value="resolvido">Resolvidos</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filters.categoria} onValueChange={(v) => setFilters(f => ({ ...f, categoria: v === 'all' ? '' : v }))}>
                  <SelectTrigger data-testid="filter-categoria">
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {CATEGORIAS.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between font-normal" data-testid="filter-motivo-pendencia">
                      <span className="truncate">
                        {Array.isArray(filters.motivo_pendencia) && filters.motivo_pendencia.length > 0
                          ? filters.motivo_pendencia.length === 1
                            ? filters.motivo_pendencia[0]
                            : `${filters.motivo_pendencia.length} motivos selecionados`
                          : 'Todos Motivos'}
                      </span>
                      <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2 max-h-72 overflow-y-auto" align="start">
                    <div
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm font-medium"
                      onClick={() => setFilters(f => ({ ...f, motivo_pendencia: [] }))}
                    >
                      Todos Motivos
                    </div>
                    <Separator className="my-1" />
                    {(motivosDisponiveis.length > 0 ? motivosDisponiveis : MOTIVOS_PENDENCIA).map(m => {
                      const isChecked = Array.isArray(filters.motivo_pendencia) && filters.motivo_pendencia.includes(m);
                      return (
                        <div
                          key={m}
                          className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer"
                          onClick={() => setFilters(f => {
                            const current = Array.isArray(f.motivo_pendencia) ? f.motivo_pendencia : [];
                            return {
                              ...f,
                              motivo_pendencia: isChecked ? current.filter(x => x !== m) : [...current, m]
                            };
                          })}
                        >
                          <Checkbox checked={isChecked} className="pointer-events-none" />
                          <span className="text-sm">{m}</span>
                        </div>
                      );
                    })}
                  </PopoverContent>
                </Popover>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between font-normal" data-testid="filter-parceiro">
                      <span className="truncate">
                        {Array.isArray(filters.parceiro) && filters.parceiro.length > 0
                          ? filters.parceiro.length === 1
                            ? filters.parceiro[0]
                            : `${filters.parceiro.length} canais selecionados`
                          : 'Todos Parceiros'}
                      </span>
                      <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-2 max-h-64 overflow-y-auto" align="start">
                    <div
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm font-medium"
                      onClick={() => setFilters(f => ({ ...f, parceiro: [] }))}
                      data-testid="filter-parceiro-todos"
                    >
                      Todos Parceiros
                    </div>
                    <Separator className="my-1" />
                    {/* Só os parceiros que têm atendimento na lista filtrada (Adnéia, 30/10/2026: em todo filtro) */}
                    {parceiros.length === 0 && (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">Nenhum parceiro nesse filtro</div>
                    )}
                    {parceiros.map(p => {
                      const isChecked = Array.isArray(filters.parceiro) && filters.parceiro.includes(p);
                      return (
                        <div
                          key={p}
                          className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer"
                          onClick={() => {
                            setFilters(f => {
                              const current = Array.isArray(f.parceiro) ? f.parceiro : [];
                              return {
                                ...f,
                                parceiro: isChecked
                                  ? current.filter(x => x !== p)
                                  : [...current, p]
                              };
                            });
                          }}
                          data-testid={`filter-parceiro-${p.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <Checkbox checked={isChecked} className="pointer-events-none" />
                          <span className="text-sm">{p}</span>
                        </div>
                      );
                    })}
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-wider font-medium bg-muted/50 w-8"></TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium bg-muted/50">Entrega</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium bg-muted/50">Cliente</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium bg-muted/50">Parceiro / Solicitação</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium bg-muted/50">Motivo Pend.</TableHead>
                  <TableHead
                    className="text-xs uppercase tracking-wider font-medium bg-muted/50 min-w-[180px] cursor-pointer select-none hover:bg-muted/80 transition-colors"
                    onClick={() => handleSort('anotacao')}
                    title="Ordenar por data da última anotação"
                  >
                    <span className="flex items-center">Última Anotação<SortIcon col="anotacao" /></span>
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium bg-muted/50">Reversa</TableHead>
                  <TableHead
                    className="text-xs uppercase tracking-wider font-medium bg-muted/50 cursor-pointer select-none hover:bg-muted/80 transition-colors"
                    onClick={() => handleSort('status_pedido')}
                    title="Ordenar por data do status do pedido"
                  >
                    <span className="flex items-center">Status Pedido<SortIcon col="status_pedido" /></span>
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium bg-muted/50">Status</TableHead>
                  <TableHead
                    className="text-xs uppercase tracking-wider font-medium bg-muted/50 cursor-pointer select-none hover:bg-muted/80 transition-colors"
                    onClick={() => handleSort('dias')}
                    title="Ordenar por dias em aberto"
                  >
                    <span className="flex items-center">Dias<SortIcon col="dias" /></span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedAtendimentos.length > 0 ? (
                  sortedAtendimentos.slice(0, limiteLinhas).map((atd) => {
                    // Determinar qual filtro está ativo para passar na navegação
                    const activeFilter = filters.retornar_chamado === 'true' ? 'retornar' :
                                        filters.verificar_adneia === 'true' ? 'verificar' : '';
                    const editUrl = activeFilter
                      ? `/chamados/editar/${atd.id}?filter=${activeFilter}`
                      : `/chamados/editar/${atd.id}`;
                    const fonteStatus = fonteStatusLista(atd);

                    // Extrair última anotação (primeira linha do campo anotacoes)
                    const ultimaAnotacao = atd.anotacoes ? atd.anotacoes.split('\n')[0] : '-';
                    // Extrair data da anotação - suporta formatos:
                    // [DD/MM/YYYY] texto, [DD/MM] texto, DD/MM - texto, DD/MM/YYYY - texto
                    let dataAnotacao = '';
                    let textoAnotacao = ultimaAnotacao;

                    // Formato com colchetes: [09/03/2026] ou [9/3]
                    const matchColchetes = ultimaAnotacao.match(/^\[(\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)\]\s*(.*)$/);
                    // Formato sem colchetes: 09/03 - texto ou 9/3 - texto
                    const matchSemColchetes = ultimaAnotacao.match(/^(\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)\s*[-–]\s*(.*)$/);

                    if (matchColchetes) {
                      dataAnotacao = matchColchetes[1];
                      textoAnotacao = matchColchetes[2] || '';
                    } else if (matchSemColchetes) {
                      dataAnotacao = matchSemColchetes[1];
                      textoAnotacao = matchSemColchetes[2] || '';
                    }

                    return (
                    <TableRow
                      key={atd.id}
                      className="hover:bg-muted/50 transition-colors"
                      data-testid={`row-${atd.id}`}
                    >
                      {/* Coluna Abrir - botão para abrir atendimento */}
                      <TableCell className="p-1">
                        <div className="flex items-center gap-0.5">
                          <a
                            href={editUrl}
                            onClick={(e) => {
                              if (!e.ctrlKey && !e.metaKey && e.button === 0) {
                                e.preventDefault();
                                navigate(editUrl);
                              }
                            }}
                            className="p-1.5 rounded hover:bg-primary/10 inline-flex items-center justify-center"
                            title="Abrir atendimento (Ctrl+Click abre em nova aba)"
                          >
                            <ExternalLink className="h-4 w-4 text-primary" />
                          </a>
                          {atd.pendente && pedidosDuplicados.has(atd.numero_pedido) && (
                            <button
                              onClick={(e) => handleOpenMerge(e, atd)}
                              className="p-1.5 rounded hover:bg-amber-100 inline-flex items-center justify-center"
                              title="Mesclar atendimentos duplicados"
                            >
                              <GitMerge className="h-4 w-4 text-amber-500" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                      {/* Coluna Entrega - copiável ao clicar */}
                      <TableCell
                        className="font-medium cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-950/30"
                        onClick={(e) => copyText(atd.numero_pedido, 'Entrega', e)}
                        title="Clique para copiar"
                      >
                        {atd.numero_pedido}
                        {(() => {
                          const c = cancelMap[String(atd.numero_pedido || '').split('.')[0]];
                          if (c?.has_cancelamento) {
                            const tp = (c.tipos || []).map(t => t === 'aes' ? 'AES' : t === 'etr' ? 'ETR' : 'Erro Nota').join(', ');
                            return (
                              <span className="mt-0.5 block w-fit text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                title={`Consta na lista de Cancelamentos${tp ? ' (' + tp + ')' : ''}`}>
                                🔔 Cancelamento{tp ? ` · ${tp}` : ''}
                              </span>
                            );
                          }
                          if (c?.aviso_movimentou) {
                            return (
                              <span className="mt-0.5 block w-fit text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                title="Estava para cancelamento, mas movimentou — verificar se o cliente foi acionado">
                                ⚠️ Movimentou
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </TableCell>
                      {/* Coluna Cliente - copiável ao clicar */}
                      <TableCell
                        className="text-sm cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-950/30"
                        onClick={(e) => copyText(`${atd.nome_cliente || ''}\n${atd.cpf_cliente || ''}`.trim(), 'Cliente', e)}
                        title="Clique para copiar"
                      >
                        <div>
                          <p className="font-medium truncate max-w-32">{atd.nome_cliente || '-'}</p>
                          {atd.cpf_cliente && (
                            <p className="text-xs text-muted-foreground">{atd.cpf_cliente}</p>
                          )}
                        </div>
                      </TableCell>
                      {/* Coluna Parceiro/Solicitação - ambos copiáveis */}
                      <TableCell className="text-sm">
                        <div className="space-y-1">
                          <p
                            className="cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-950/30 px-1 -mx-1 rounded"
                            onClick={(e) => copyText(atd.parceiro || atd.canal_vendas, 'Parceiro', e)}
                            title="Clique para copiar parceiro"
                          >
                            {atd.parceiro || atd.canal_vendas || '-'}
                          </p>
                          {atd.solicitacao && (
                            <p
                              className="text-xs text-primary cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-950/30 px-1 -mx-1 rounded"
                              onClick={(e) => copyText(atd.solicitacao, 'Solicitação', e)}
                              title="Clique para copiar solicitação"
                            >
                              {atd.solicitacao}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      {/* Coluna Motivo Pendência - copiável */}
                      <TableCell
                        className="text-sm cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-950/30"
                        onClick={(e) => copyText(atd.motivo_pendencia, 'Motivo', e)}
                        title="Clique para copiar"
                      >
                        {atd.motivo_pendencia || '-'}
                      </TableCell>
                      {/* Coluna Última Anotação - copiável */}
                      <TableCell
                        className="text-sm max-w-[200px] cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-950/30"
                        onClick={(e) => copyText(atd.anotacoes ? atd.anotacoes.split('\n')[0] : '', 'Anotação', e)}
                        title="Clique para copiar última anotação"
                      >
                        <div className="flex flex-col">
                          {dataAnotacao && (
                            <span className="font-semibold text-blue-600 dark:text-blue-400 text-xs">{dataAnotacao}</span>
                          )}
                          {textoAnotacao ? (
                            <span className="text-muted-foreground text-xs truncate" title={textoAnotacao}>
                              {textoAnotacao}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </div>
                      </TableCell>
                      {/* Coluna Reversa - copiável + alerta de vencimento */}
                      <TableCell
                        className="text-sm font-mono cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-950/30"
                        onClick={(e) => copyText(atd.codigo_reversa, 'Reversa', e)}
                        title="Clique para copiar"
                      >
                        <div className="flex flex-col gap-0.5">
                          <span>{atd.codigo_reversa || '-'}</span>
                          {atd.codigo_reversa && (() => {
                            // Postada
                            if (atd.reversa_postada) {
                              return (
                                <span className="text-[11px] text-green-600 font-medium">
                                  ✓ Postada {atd.data_postagem_reversa
                                    ? new Date(atd.data_postagem_reversa + 'T00:00:00').toLocaleDateString('pt-BR', {day:'2-digit', month:'2-digit', year:'numeric'})
                                    : '· sem data'}
                                </span>
                              );
                            }
                            // Sem data de vencimento
                            if (!atd.data_vencimento_reversa) {
                              return <span className="text-[11px] text-gray-400">Sem data de validade</span>;
                            }
                            // Parse data vencimento
                            const dv = atd.data_vencimento_reversa;
                            let dataVenc;
                            if (dv.includes('-')) dataVenc = new Date(dv + 'T00:00:00');
                            else if (dv.includes('/')) {
                              const p = dv.split('/');
                              if (p.length === 3) dataVenc = new Date(p[2], p[1]-1, p[0]);
                              else if (p.length === 2) dataVenc = new Date(2026, p[1]-1, p[0]);
                            }
                            if (!dataVenc || isNaN(dataVenc)) return null;
                            dataVenc.setHours(0,0,0,0);
                            const hoje = new Date(); hoje.setHours(0,0,0,0);
                            const diffDias = Math.ceil((dataVenc - hoje) / (1000*60*60*24));
                            const dataFormatada = dataVenc.toLocaleDateString('pt-BR', {day:'2-digit', month:'2-digit', year:'numeric'});
                            // Vencida
                            if (diffDias < 0) {
                              return (
                                <span className="text-[11px] text-red-600 font-medium">
                                  {dataFormatada} · Vencida
                                </span>
                              );
                            }
                            // Vence em breve (≤4 dias)
                            if (diffDias <= 4) {
                              return (
                                <span className="text-[11px] text-amber-600 font-medium">
                                  {dataFormatada} · Vence em {diffDias}d
                                </span>
                              );
                            }
                            // Normal - só mostra a data
                            return <span className="text-[11px] text-gray-400">{dataFormatada}</span>;
                          })()}
                          {/* Checkpoint Aguardando: cliente postou → Em devolução + planilha */}
                          {atd.motivo_pendencia === 'Aguardando' && atd.codigo_reversa && !atd.reversa_postada && (
                            <button
                              onClick={(e) => marcarPostado(atd, e)}
                              title="Cliente postou o item → move para Em devolução e registra na planilha"
                              className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-200 text-[10px] font-semibold w-fit"
                            >
                              📦 Marcar postado
                            </button>
                          )}
                        </div>
                      </TableCell>
                      {/* Coluna Status Pedido - copiável + AJUSTE 5: data abaixo. Status e data do
                          BSeller desde 27/10/2026; "do tabelão" quando o BSeller não respondeu. */}
                      <TableCell
                        className="text-sm cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-950/30"
                        onClick={(e) => copyText(atd.status_pedido, 'Status', e)}
                        title={[fonteStatus.titulo, 'Clique para copiar'].filter(Boolean).join('\n')}
                      >
                        <div className="flex flex-col">
                          <span>{atd.status_pedido || '-'}</span>
                          {atd.data_ultimo_status && (
                            <span className="text-[11px] text-gray-400">
                              {(() => {
                                const d = atd.data_ultimo_status;
                                if (!d) return '';
                                // Se já está em DD/MM/YYYY, extrair apenas a parte da data
                                if (d.includes('/')) return d.split(' ')[0];
                                // Se é ISO format, converter
                                try {
                                  const date = new Date(d);
                                  if (!isNaN(date)) return date.toLocaleDateString('pt-BR');
                                } catch {}
                                return d.split(' ')[0];
                              })()}
                            </span>
                          )}
                          {fonteStatus.marca && (
                            <span className={`text-[10px] ${fonteStatus.alerta ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'}`}>
                              {fonteStatus.marca}
                            </span>
                          )}
                          {travadoEnviado(atd) && (
                            <span className="mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-300 text-[10px] font-semibold w-fit"
                              title={`${diasTravado(atd)} dias úteis sem retorno/movimentação — verificar`}>
                              ⚠️ Travado {diasTravado(atd)}d
                            </span>
                          )}
                        </div>
                      </TableCell>
                      {/* Coluna Status do Atendimento */}
                      <TableCell
                        className="cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-950/30"
                        onClick={(e) => {
                          const partes = [];
                          if (atd.retornar_chamado) partes.push('Retornar');
                          if (atd.verificar_adneia) partes.push('Verificar');
                          partes.push(atd.pendente ? 'Pendente' : 'Resolvido');
                          copyText(partes.join(', '), 'Status', e);
                        }}
                        title="Clique para copiar"
                      >
                        {atd.retornar_chamado && (
                          <Badge className="bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-400 mr-1">
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Retornar
                          </Badge>
                        )}
                        {atd.verificar_adneia && (
                          <Badge className="bg-purple-50 text-purple-700 dark:bg-purple-950/50 dark:text-purple-400 mr-1">
                            Verificar
                          </Badge>
                        )}
                        {atd.pendente ? (
                          <Badge className="bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400">
                            Pendente
                          </Badge>
                        ) : (
                          <Badge className="bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400">
                            Resolvido
                          </Badge>
                        )}
                        {/* Checkbox inline: marca como Verificar sem abrir o atendimento */}
                        {atd.pendente && (
                          <label
                            className="mt-1 flex items-center gap-1 text-[11px] text-purple-700 cursor-pointer w-fit"
                            onClick={(e) => e.stopPropagation()}
                            title="Marcar/desmarcar Verificar"
                          >
                            <input
                              type="checkbox"
                              checked={!!atd.verificar_adneia}
                              onChange={(e) => toggleVerificar(atd, e)}
                              className="cursor-pointer"
                            />
                            Verificar
                          </label>
                        )}
                      </TableCell>
                      {/* Coluna Dias */}
                      <TableCell
                        className={`text-sm cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-950/30 ${atd.dias_aberto > 3 ? 'text-orange-600 font-medium' : ''}`}
                        onClick={(e) => copyText(String(atd.dias_aberto ?? ''), 'Dias', e)}
                        title="Clique para copiar"
                      >
                        {atd.dias_aberto}
                      </TableCell>
                    </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} className="h-32 text-center text-muted-foreground">
                      Nenhum atendimento encontrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {sortedAtendimentos.length > limiteLinhas && (
            <div className="flex flex-wrap items-center justify-center gap-3 border-t px-4 py-3 text-sm text-muted-foreground" data-testid="mais-linhas">
              <span>Mostrando {limiteLinhas} de {sortedAtendimentos.length} atendimentos</span>
              <Button variant="outline" size="sm" onClick={() => setLimiteLinhas(n => n + LINHAS_POR_VEZ)}>
                Mostrar mais {Math.min(LINHAS_POR_VEZ, sortedAtendimentos.length - limiteLinhas)}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setLimiteLinhas(sortedAtendimentos.length)}
                title="Desenha todos de uma vez: com muitos, a tela fica lenta">
                Mostrar todos
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal: Acionar Parceiro */}
      {acionarData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setAcionarData(null)}>
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-5xl mx-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="font-bold text-base flex items-center gap-2">📨 Acionar Parceiro</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {qtdCancAcionar > 0
                    ? `Pendências dos atendimentos filtrados (${acionarData.length - qtdCancAcionar}) e cancelamentos AES com ticket (${qtdCancAcionar}) — para acionar o canal.`
                    : `Pendências dos atendimentos filtrados (${acionarData.length}) — para solicitar encerramento ao canal.`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={copiarAcionar}>
                  <Copy className="h-4 w-4 mr-1" /> Copiar
                </Button>
                <Button size="sm" variant="outline" onClick={exportarAcionar}>
                  <Download className="h-4 w-4 mr-1" /> Excel
                </Button>
                <button onClick={() => setAcionarData(null)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
              </div>
            </div>
            <div className="overflow-auto flex-1">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Parceiro</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Tipo</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Entrega</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Pedido</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Solicitação</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">CPF</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Data</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Última Anotação</th>
                  </tr>
                </thead>
                <tbody>
                  {acionarData.map((r, idx) => (
                    <tr key={idx} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800/30">
                      <td className="px-3 py-1.5">{r.parceiro}</td>
                      <td className={`px-3 py-1.5 whitespace-nowrap text-xs ${r.tipo === TIPO_CANCELAMENTO_AES ? 'font-semibold text-purple-700 dark:text-purple-300' : 'text-slate-500'}`}>{r.tipo}</td>
                      <td className="px-3 py-1.5 font-mono text-xs">{r.entrega}</td>
                      <td className="px-3 py-1.5 font-mono text-xs">{r.pedido}</td>
                      <td className="px-3 py-1.5 font-mono text-xs">{r.solicitacao}</td>
                      <td className="px-3 py-1.5 font-mono text-xs">{r.cpf}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap">{r.data_anotacao}</td>
                      <td className="px-3 py-1.5 max-w-[280px] truncate" title={r.anotacao}>{r.anotacao}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 dark:bg-slate-800 font-semibold">
                    <td colSpan={7} className="px-3 py-2 text-right">Total:</td>
                    <td className="px-3 py-2">{acionarData.length}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListaAtendimentos;
